import { Button, Tooltip, toast } from "@/components/ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/Tabs";
import { RxHistoryButton } from "@/features/Patient/MedicalRecords/RxHistory/RxHistoryButton";

import {
  CheckCheck,
  CircleOff,
  CirclePlus,
  LoaderCircle,
  Tablets,
  TriangleAlert,
} from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/Dropdown";
import { TRACKERS } from "@/constants/Trackers";
import { SectionContent } from "@/features/Patient/MedicalRecords/components/SectionContent";
import { trackEvent } from "@/lib/tracking";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";
import { SectionHeader } from "../components/SectionHeader";
import { AddAllergyForm } from "./components/allergies/AddAllergyForm";
import { AllergiesSection } from "./components/allergies/AllergiesSection";
import { AddMultipleMedicationsForm } from "./components/medications/AddMultipleMedicationsForm";
import { MedicationsSection } from "./components/medications/MedicationsSection";
import {
  AllergyStatus,
  useBatchConfirmAllergies,
  useMarkPatientNKDA,
} from "./network/useAllergies";
import {
  AdherenceStatus,
  useBatchConfirmMedications,
} from "./network/useMedications";
import { useAvailableChartingActions } from "./useAvailableChartingActions";

export const MedicationsAllergies = () => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const [showAddMedicationForm, setShowAddMedicationForm] = useState(false);
  const [showAddAllergyForm, setShowAddAllergyForm] = useState(false);

  const {
    isBatchConfirmAvailable,
    isNKDAAvailable,
    isAddMedicationAvailable,
    isAddAllergyAvailable,
    activeAllergies,
    activeMedications,
  } = useAvailableChartingActions({ patientId });

  const {
    mutate: batchConfirmMedications,
    isPending: isBatchConfirmingMedications,
  } = useBatchConfirmMedications({
    patientId,
  });
  const {
    mutate: batchConfirmAllergies,
    isPending: isBatchConfirmingAllergies,
  } = useBatchConfirmAllergies({
    patientId,
  });

  const { mutate: markNKDA, isPending: isNKDASaving } = useMarkPatientNKDA({
    patientId,
    onError: ({ message }) => {
      toast.error(message);
    },
  });

  const actions = [<RxHistoryButton patientId={patientId} key="rx-history" />];
  if (isBatchConfirmAvailable) {
    actions.push(
      <Tooltip tooltipContentText="Confirm All" side="bottom" key="confirm-all">
        <Button
          variant="icon"
          className="m-0 p-0"
          data-testid="confirm-all"
          onClick={() => {
            if (isBatchConfirmingAllergies || isBatchConfirmingMedications) {
              return;
            }

            batchConfirmMedications(
              activeMedications?.map(
                (medication) => medication.medicationStatement.id,
              ) as string[],
            );

            batchConfirmAllergies(
              activeAllergies?.allergies?.map(
                (allergy) => allergy.allergyIntolerance.id,
              ) as string[],
            );
          }}
        >
          {isBatchConfirmingAllergies || isBatchConfirmingMedications ? (
            <LoaderCircle className="stroke-link h-4 w-4 animate-spin" />
          ) : (
            <CheckCheck className="stroke-link h-4 w-4" />
          )}
        </Button>
      </Tooltip>,
    );
  }

  if (isNKDAAvailable) {
    actions.push(
      <Tooltip
        tooltipContentText="Mark No Known Drug Allergies"
        side="bottom"
        key="nkda"
      >
        <Button
          variant="icon"
          className="m-0 p-0"
          data-testid="mark-nkda"
          onClick={() => {
            trackEvent(TRACKERS.MARK_NO_KNOWN_DRUG_ALLERGIES);
            if (isNKDASaving) {
              return;
            }
            markNKDA();
          }}
        >
          {isNKDASaving ? (
            <LoaderCircle className="stroke-link h-4 w-4 animate-spin" />
          ) : (
            <CircleOff className="stroke-link h-4 w-4" />
          )}
        </Button>
      </Tooltip>,
    );
  }

  if (isAddMedicationAvailable || isAddAllergyAvailable) {
    actions.push(
      <DropdownMenu key="add">
        <DropdownMenuTrigger asChild>
          <Button
            variant="icon"
            className="m-0 p-0 items-start pt-0.5 h-4 w-4"
            data-testid="add-med-allergy"
          >
            <Tooltip tooltipContentText="Add" side="bottom" key="add">
              <CirclePlus className="stroke-link h-4 w-4" />
            </Tooltip>
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="start"
          className="w-fit-content"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <DropdownMenuGroup>
            {isAddMedicationAvailable && (
              <DropdownMenuItem
                key="addMedication"
                onClick={() => {
                  trackEvent(TRACKERS.ADD_MEDICATION);
                  setShowAddAllergyForm(false);
                  setShowAddMedicationForm(true);
                }}
              >
                <Tablets size={16} className="mr-2" />
                Add Medications
              </DropdownMenuItem>
            )}
            {isAddAllergyAvailable && (
              <DropdownMenuItem
                key="addAllergy"
                onClick={() => {
                  setShowAddMedicationForm(false);
                  setShowAddAllergyForm(true);
                }}
              >
                <TriangleAlert size={16} className="mr-2" /> Add Allergy
              </DropdownMenuItem>
            )}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>,
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      <SectionHeader
        title="Medications & Allergies"
        actionButtons={actions}
        showSeparator={false}
      />
      <SectionContent>
        {showAddMedicationForm && (
          <AddMultipleMedicationsForm
            closeForm={() => {
              setShowAddMedicationForm(false);
            }}
          />
        )}
        {showAddAllergyForm && (
          <AddAllergyForm closeForm={() => setShowAddAllergyForm(false)} />
        )}
        <Tabs defaultValue="active">
          <TabsList className="mb-4">
            <TabsTrigger value="active" className="gap-1.5">
              Active
            </TabsTrigger>
            <TabsTrigger value="stopped">Resolved</TabsTrigger>
          </TabsList>
          <TabsContent value="active" className="flex flex-col gap-2">
            <AllergiesSection
              status={AllergyStatus.ACTIVE}
              isBatchConfirmingAllergies={isBatchConfirmingAllergies}
            />
            <MedicationsSection
              adherenceStatus={AdherenceStatus.ACTIVE}
              isBatchConfirmingMedications={isBatchConfirmingMedications}
            />
          </TabsContent>
          <TabsContent value="stopped" className="flex flex-col gap-2">
            <AllergiesSection
              status={AllergyStatus.INACTIVE}
              isBatchConfirmingAllergies={isBatchConfirmingAllergies}
            />
            <MedicationsSection
              adherenceStatus={AdherenceStatus.STOPPED}
              isBatchConfirmingMedications={isBatchConfirmingMedications}
            />
          </TabsContent>
        </Tabs>
      </SectionContent>
    </div>
  );
};
